<template>
  <div class="content mb-5">
    <b-row class="mb-5">
      <b-col>
        <t-data-table
          data-test-id="policies-data-table"
          :title="$t('All Policies')"
          with-filter
          hoverable
          :filter-placeholder="$t('Search Policies')"
          :loading="loading"
          :fetching="fetching"
          :table-data="dataSet"
          :actions="tableActions"
          :searchExports="searchExports"
          :server-items-length="serverItemsLength"
          user-role="customer"
          @on-delete="onDelete"
          @on-add="onAdd"
          @on-export="onExport"
          @export-headers="exportedHeaders"
          @on-row-clicked="onRowClicked"
          @async-paginate="onSearch"
        />
      </b-col>
    </b-row>
  </div>
</template>

<route>
{
  "name": "Customer Policies",
  "meta": {
    "authorize" : [1, 3, 4]
  }
}
</route>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

export default {
  layout: "customer",
  data: function () {
    return {
      loading: false,
      fetching: false,
      currentHeaders: null,
      searchExports: ["name", "description"],
      serverItemsLength: 0,
      dataOptions: {
        results: 10,
        page: 1,
        sortBy: "name",
        sortDesc: "asc",
        filter: null,
      },

      dataSet: {
        fields: [
          { value: "name", text: this.$t("Policy"), sortable: true },
          {
            value: "description",
            text: this.$t("Description"),
            sortable: false,
          },
          {
            value: "first_location",
            text: this.$t("Location"),
            sortable: true,
          },
          {
            value: "last_updated",
            text: this.$t("Last Updated"),
            sortable: true,
          },
        ],
        items: [],
      },
      tableActions: {
        add: {
          enabled: true,
          actions: [{}],
        },
        import: { enabled: false },
        export: { enabled: true },
        delete: { enabled: true },
      },
    };
  },
  watch: {
    customerPolicies() {
      this.dataSet.items = JSON.parse(JSON.stringify(this.customerPolicies));
    },
    serverTotal: {
      handler() {
        this.serverItemsLength = this.serverTotal;
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters("customerPolicies", [
      "customerPolicies",
      "serverTotal",
      "exportPolicies",
    ]),
    ...mapGetters("authentication", ["selectedUser"]),
  },
  methods: {
    ...mapMutations("modal", ["createModal", "showModal"]),
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("customerPolicies", [
      "fetchCustomerPolicies",
      "deleteCustomerPolicies",
      "exportCustomerPolicies",
    ]),
    async onDelete(policies) {
      try {
        this.loading = true;
        await this.deleteCustomerPolicies({
          userId: this.selectedUser.id,
          policies,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Deleted: X", { item: policies.length }),
          duration: 2000,
          variant: "success",
        });
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      } finally {
        this.loading = false;
      }
    },
    onAdd({ name, modal, type }) {
      this.createModal({
        modal,
        dataTestId: "modal-add-policy",
        title: this.$t("Create Policy"),
        subtitle: name,
        modalData: {
          type,
        },
        props: {
          size: "lg",
          okTitle: this.$t("Create Policy"),
        },
      });

      this.showModal();
    },
    onImport(items) {
      console.log("Import items:", items); // eslint-disable-line no-console
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    async onExport() {
      try {
        this.fetching = true;
        await this.exportCustomerPolicies({
          id: this.selectedUser.id,
          show_default: "false",
        });
      } finally {
        this.fetching = false;
      }

      if (this.exportPolicies.length) {
        const exportData = this.exportPolicies.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[headers.value] = res[headers.value]);
          });

          return payload;
        });
        this.$downloadCSV(exportData, "Polices");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No Polices in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    onRowClicked(item) {
      this.$router.push({
        path: `/customer/content_filtering/policies/${item.id}`,
      });
    },
    async onSearch(options) {
      if (options.itemsPerPage === undefined) return;
      if (options.itemsPerPage) {
        this.dataOptions.results = options.itemsPerPage;
      }
      if (options.page) {
        this.dataOptions.page = options.page;
      }
      if (options.sortBy) {
        this.dataOptions.sortBy = options.sortBy[0];
      }
      if (options.sortDesc) {
        this.dataOptions.sortDesc = options.sortDesc;
      }

      if (this.dataOptions.filter !== options.filter) {
        this.dataOptions.page = 1;
      }
      this.dataOptions.filter = options.filter;

      try {
        this.fetching = true;
        await this.fetchCustomerPolicies({
          id: this.selectedUser.id,
          show_default: "false",
          ...this.dataOptions,
        });
      } finally {
        this.fetching = false;
      }
    },
  },

  created() {
    this.setPageTitle(this.$t("Policies"));
    this.setPageSubtitle([
      this.$t(
        "Policies determine what categories of website a user can see. The Default Policy in Settings is automatically given to a new user, but you can create your own custom policies here and assign them to your locations, devices, groups, or users."
      ),
    ]);

    this.tableActions.add = {
      enabled: true,
      actions: [
        {
          text: this.$t("Create Policy"),
          value: {
            name: this.selectedUser.accountname || "",
            modal: "t-policies-add",
            type: "add",
          },
        },
      ],
    };
  },
};
</script>
